import React, { useMemo } from "react";
import clsx from "clsx";

import {
  getStudentAnswersByQuestion,
  getLessonFrom,
  getIssueFrom,
  getQuestionFrom,
} from "../../../../libs/ActiveLearning";
import {
  IActiveLearningMaterialLessonsData,
  IActiveLearningStudentAnswers,
  IActiveLearningAllAnswers,
} from "../../../../state";
import styles from "../LessonsAnswerData.module.scss";

type Lesson4Pros = {
  materialCode: string;
  lessonNo: number;
  activeLearningMaterialLessons: IActiveLearningMaterialLessonsData[];
  activeLearningAnswers:
    | IActiveLearningStudentAnswers
    | IActiveLearningAllAnswers
    | null;
};

export const Lesson4: React.FC<Lesson4Pros> = ({
  materialCode,
  lessonNo,
  activeLearningMaterialLessons,
  activeLearningAnswers,
}) => {
  const activeLearningMaterialLessonsByCode = useMemo(() => {
    return activeLearningMaterialLessons.filter(
      data => data.code === materialCode,
    );
  }, [activeLearningMaterialLessons, materialCode]);

  const titleL4 = useMemo(() => {
    return getLessonFrom(activeLearningMaterialLessonsByCode, lessonNo)?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL4I1 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 1)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL4I2 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 2)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL4I3 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 3)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL4I3Q2 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 3, 2)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const answersL4I1 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      1,
    );
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL4I2 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
    );
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL4I3Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      3,
      1,
    );

    return answers.map(answer => answer.contents.value).join("");
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL4I3Q2 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      3,
      2,
    );

    return answers.map(answer => answer.contents.value).join("");
  }, [materialCode, activeLearningAnswers, lessonNo]);

  return (
    <div className={styles.lesson}>
      <p className={styles.title}>
        Lesson{lessonNo < 10 ? `0${lessonNo}` : lessonNo} {titleL4}
      </p>
      <div className={styles.inner}>
        <div className={styles.work}>
          <p className={styles.no}>WORK01</p>
          {answersL4I1.length > 0 ? (
            answersL4I1.map((answer, index) => (
              <div key={answer.id} className={styles.question}>
                {index === 0 && <p className={styles.heading}>{titleL4I1}</p>}
                <div className={styles.tree}>
                  <p className={styles.answer}>
                    {answer.contents.subject || "回答データがありません。"}
                  </p>
                  <ul className={styles.branchList}>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>原因</span>
                      </div>
                      <p className={styles.answer}>
                        {answer.contents.value || "回答データがありません。"}
                      </p>
                    </li>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>調べたこと</span>
                      </div>
                      <p className={clsx(styles.answer, styles.high)}>
                        {answer.contents.research || "回答データがありません。"}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.question}>
              <p className={styles.heading}>{titleL4I1}</p>
              <div className={styles.tree}>
                <p className={styles.answer}>回答データがありません。</p>
                <ul className={styles.branchList}>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>原因</span>
                    </div>
                    <p className={styles.answer}>回答データがありません。</p>
                  </li>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>調べたこと</span>
                    </div>
                    <p className={clsx(styles.answer, styles.high)}>
                      回答データがありません。
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK02</p>
          {answersL4I2.length > 0 ? (
            answersL4I2.map((answer, index) => (
              <div key={answer.id} className={styles.question}>
                {index === 0 && <p className={styles.heading}>{titleL4I2}</p>}
                <div className={styles.tree}>
                  <p className={styles.answer}>
                    {answer.contents.subject || "回答データがありません。"}
                  </p>
                  <ul className={styles.branchList}>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>原因</span>
                      </div>
                      <p className={styles.answer}>
                        {answer.contents.reason || "回答データがありません。"}
                      </p>
                    </li>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>理想状況</span>
                      </div>
                      <p className={styles.answer}>
                        {answer.contents.value || "回答データがありません。"}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.question}>
              <p className={styles.heading}>{titleL4I2}</p>
              <div className={styles.tree}>
                <p className={styles.answer}>回答データがありません。</p>
                <ul className={styles.branchList}>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>原因</span>
                    </div>
                    <p className={styles.answer}>回答データがありません。</p>
                  </li>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>理想状況</span>
                    </div>
                    <p className={styles.answer}>回答データがありません。</p>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK03</p>
          <div className={styles.question}>
            <p className={styles.heading}>{titleL4I3}</p>
            <p className={styles.answer}>
              {answersL4I3Q1 || "回答データがありません。"}
            </p>
          </div>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>{titleL4I3Q2}</p>
            <p className={clsx(styles.answer, styles.high)}>
              {answersL4I3Q2 || "回答データがありません。"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Lesson4;
